import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./LidarSidewallMesh.module.less";
import { Canvas, ThreeEvent } from "@react-three/fiber";
import {
  OrbitControls,
  PerspectiveCamera,
  Plane,
  useBVH,
} from "@react-three/drei";
import {
  BufferGeometry,
  DoubleSide,
  DynamicDrawUsage,
  LinearToneMapping,
  Material,
  Mesh,
  NormalBufferAttributes,
  Object3DEventMap,
  SRGBColorSpace,
} from "three";
import { getUnit } from "~/helpers/userPreference";
import { UNITS } from "~/constants/unit";
import {
  calculateDelaunayDataForSidewall,
  getMeshTooltip,
} from "../../../../helpers/meshCalculator";
import { LidarDataVisualizationZone } from "../../../../../../types/getLiDARDataVisualizationSummariesForLastSpecificHeats";
import { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { Button } from "antd";
interface LidarSidewallScatterProps {
  visualizationZoneData: LidarDataVisualizationZone[];
  isExpand: boolean;
}

const LidarSidewallMesh: React.FC<LidarSidewallScatterProps> = ({
  visualizationZoneData,
  isExpand,
}) => {
  const lidarSidewallMeshRef = useRef<HTMLDivElement>(null);
  //Fatih, INFO:
  //Need just rerender <mesh> comp
  //If rerender whole component it has blink effect during slider plays

  //INFO: data is a huge array and it is not necessary to check it for changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const meshKey = useMemo(() => Math.random(), [visualizationZoneData.length]);

  //Fatih, INFO:
  //TooltipRef send to mesh component for tooltip
  //To get better position x and y
  const tooltipRef = useRef<HTMLSpanElement | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const controlsRef = useRef<OrbitControlsImpl | null>(null);

  const [isZoomed, setIsZoomed] = useState<boolean>(false);

  const clearTooltip = useCallback(() => {
    if (tooltipRef.current && canvasRef.current) {
      tooltipRef.current.style.display = "none";
      canvasRef.current.style.cursor = "default";
    }
  }, []);

  const handleResetCamera = useCallback(() => {
    if (controlsRef.current) {
      controlsRef.current.reset();
      controlsRef.current.update();
      setIsZoomed(false);
    }
  }, []);

  const handleRightClick = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if ((event.ctrlKey && event.button === 0) || event.button === 2) {
        //Right click and ctrl + leftclick
        event.preventDefault();
        setIsZoomed(true);
      }
    },
    [],
  );

  useEffect(() => {
    //COMMENT: SUAT, Bug 13713: Point information is not displayed correctly in the expanded display.
    const lidarSidewallMesh = lidarSidewallMeshRef.current;

    if (!isExpand && lidarSidewallMesh) {
      lidarSidewallMesh.addEventListener("mouseout", clearTooltip);
    }

    return () => {
      if (lidarSidewallMesh) {
        lidarSidewallMesh.removeEventListener("mouseout", clearTooltip);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpand]);

  return (
    <div className={styles.lidarSidewallMesh} ref={lidarSidewallMeshRef}>
      {isZoomed && isExpand && (
        <Button
          className={styles.resetCameraButton}
          size={"large"}
          onClick={handleResetCamera}
        >
          Reset Camera
        </Button>
      )}
      <span className={styles.tooltip} ref={tooltipRef} />

      <Canvas
        ref={canvasRef}
        onWheel={() => {
          setIsZoomed(true);
        }}
        onPointerDown={handleRightClick}
        gl={{
          outputColorSpace: SRGBColorSpace,
          toneMapping: LinearToneMapping,
        }}
        onMouseOut={clearTooltip}
      >
        <PerspectiveCamera
          makeDefault
          position={[0, 0, 25]}
          rotation={[0, 0, 0]}
        />
        <spotLight position={[0, 0, 20]} color={"#ffffff"} intensity={2000} />
        <Plane args={[1, 1]} scale={isExpand ? [1.4, 1.2, 1.5] : [1, 1, 1]}>
          <SidewallMesh
            tooltipRef={tooltipRef}
            canvasRef={canvasRef}
            visualizationZoneData={visualizationZoneData}
            key={meshKey}
            isExpanded={isExpand}
            clearTooltip={clearTooltip}
          />
        </Plane>
        <OrbitControls
          ref={controlsRef}
          enableRotate={false}
          maxDistance={25}
          enableZoom={isExpand}
        />
      </Canvas>
    </div>
  );
};

export default LidarSidewallMesh;

interface SidewallMeshProps {
  visualizationZoneData: LidarDataVisualizationZone[];
  tooltipRef: React.MutableRefObject<HTMLSpanElement | null>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  isExpanded: boolean;
  clearTooltip: () => void;
}

const SidewallMesh: FC<SidewallMeshProps> = ({
  visualizationZoneData,
  tooltipRef,
  canvasRef,
  isExpanded,
  clearTooltip,
}) => {
  const meshRef = useRef<Mesh>();

  useBVH(meshRef);

  const { points, meshIndex, colors } = useMemo(() => {
    return calculateDelaunayDataForSidewall(visualizationZoneData);

    //INFO: data is a huge array and it is not necessary to check it for changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualizationZoneData.length]);

  const hoverHandler = (event: ThreeEvent<PointerEvent>) => {
    if (event.intersections.length === 0) {
      return;
    }

    const { course, quadrant, thickness } = getMeshTooltip(
      event.intersections[0],
      true,
    );
    requestAnimationFrame(() => {
      if (tooltipRef.current && canvasRef.current) {
        canvasRef.current.style.cursor = "crosshair";
        tooltipRef.current.style.display = "unset";
        tooltipRef.current.style.left = `${
          event.offsetX + (isExpanded ? 170 : 0)
        }px`;
        tooltipRef.current.style.top = `${
          event.offsetY + (isExpanded ? -25 : -50)
        }px`;
        tooltipRef.current.innerHTML = `<div className="${
          styles.tooltip
        }"><div>${thickness}${getUnit(
          UNITS.MILLIMETER,
        )}</div><div style="font-size: 14px">Course: ${course}</div> <div style="font-size: 14px">Clock: ${quadrant}</div></div>`;
      }
    });
  };

  return (
    <mesh
      ref={
        meshRef as React.Ref<
          Mesh<
            BufferGeometry<NormalBufferAttributes>,
            Material | Material[],
            Object3DEventMap
          >
        >
      }
      rotation={[0, 0, 0]}
      position={[0, 8, 0]}
      onPointerMove={hoverHandler}
      onPointerLeave={clearTooltip}
      onPointerOut={clearTooltip}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          array={meshIndex}
          attach="index"
          count={meshIndex ? meshIndex.length : 0}
          itemSize={1}
          usage={DynamicDrawUsage}
        />
        <bufferAttribute
          attach="attributes-position"
          array={points}
          count={points ? points.length / 3 : 0}
          itemSize={3}
          usage={DynamicDrawUsage}
        />
        <bufferAttribute
          attach="attributes-color"
          array={colors}
          count={colors ? colors.length / 3 : 0}
          itemSize={3}
          usage={DynamicDrawUsage}
        />
      </bufferGeometry>
      <meshLambertMaterial
        vertexColors
        side={DoubleSide}
        wireframe={false}
        flatShading
      />
    </mesh>
  );
};
